import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { React, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Colors from '../../colors';
import Button from '../../components/Button';
import ButtonOutline from '../../components/ButtonOutline';
import ImportCSV from '../../components/ImportCSV';
import LoadingSpinner from '../../components/LoadingSpinner';
import MemberRow from '../../components/MemberRow';
import SearchBar from '../../components/SearchBar';
import TextInput from '../../components/TextInput';
import AddMemberModal from '../../components/modals/AddMemberModal';
import EditMemberModal from '../../components/modals/EditMemberModal';
import { createNewDocInDb, createNewSubcollectionDocInDb } from '../../firebase';

function CreateTeam({ user }) {
    // Key data
    const [teamName, setTeamName] = useState('');
    const [teamMembers, setTeamMembers] = useState([]);
    const [csvData, setCsvData] = useState([]);
    const [memberRows, setMemberRows] = useState([]);

    // For 'Add Member' modal
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [notes, setNotes] = useState('');
    const [addMemberModalVisible, setAddMemberModalVisible] = useState(false);
    const openAddMemberModal = () => setAddMemberModalVisible(true);
    const closeAddMemberModal = () => setAddMemberModalVisible(false);

    // For 'Edit Member' modal
    const [editMemberModalVisible, setEditMemberModalVisible] = useState(false);
    const [editedMember, setEditedMember] = useState();
    const [editFirstName, setEditFirstName] = useState('');
    const [editLastName, setEditLastName] = useState('');
    const [editPhoneNumber, setEditPhoneNumber] = useState('');
    const [editNotes, setEditNotes] = useState('');

    // Enables Loading Spinner
    const [isLoading, setIsLoading] = useState(false);
    const [errMsg, setErrMsg] = useState();

    const openEditMemberModal = (member) => {
        setEditedMember(member);
        setEditFirstName(member.firstName);
        setEditLastName(member.lastName);
        setEditPhoneNumber(member.phoneNumber);
        setEditNotes(member.notes);
        setEditMemberModalVisible(true);
    }
    const closeEditMemberModal = () => setEditMemberModalVisible(false);

    // For Search Bar
    const [search, setSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);

    const navigate = useNavigate();

    // Use effect for turning csv data into displayed rows
    useEffect(() => {
        for (const row of csvData) {
            if (row.phoneNumber === undefined) {
                row.phoneNumber = '';
            }
            row.notes = '';
            setTeamMembers(prevArray => [...prevArray, row]);
        };
    }, [csvData]);

    // Create MemberRows whenever team members are updated
    useEffect(() => {
        setMemberRows([]);

        teamMembers.sort((memberA, memberB) => {
            const firstNameA = memberA.firstName.toLowerCase();
            const firstNameB = memberB.firstName.toLowerCase();

            if (firstNameA < firstNameB) {
                return -1; // a should be before b
            } else if (firstNameA > firstNameB) {
                return 1; // a should be after b
            }

            return 0; // a and b are equal in terms of firstName
        });

        for (const member of teamMembers) {
            const memberRow = <MemberRow
                key={member.firstName + member.lastName}
                member={member}
                onClickEdit={() => openEditMemberModal(member)} />
            setMemberRows(prevArray => [...prevArray, memberRow]);
        };
    }, [teamMembers]);

    // Use effect for filtering displayed rows by search
    useEffect(() => {
        setSearchResults([]);
        if (search.length > 0) {
            setSearchResults(memberRows.filter((memberRow) => {
                return (
                    memberRow
                        .props
                        .member
                        .firstName
                        .toLowerCase()
                        .includes(search.toLowerCase()) ||
                    memberRow
                        .props
                        .member
                        .lastName
                        .toLowerCase()
                        .includes(search.toLowerCase())
                );
            }));
        }
    }, [search, memberRows]);

    // Manually add new member using 'Add Member' button
    function addMember() {
        const newMember = {
            firstName: firstName,
            lastName: lastName,
            phoneNumber: phoneNumber,
            notes: notes,
        }
        setTeamMembers(prevArray => [...prevArray, newMember]);

        // Reset the initial member states
        setFirstName('');
        setLastName('');
        setPhoneNumber('');
        setNotes('');
        closeAddMemberModal();
    }

    // Edit member
    function editMember() {
        const newMember = {
            firstName: editFirstName,
            lastName: editLastName,
            phoneNumber: editPhoneNumber,
            notes: editNotes,
        }
        setTeamMembers(teamMembers.filter(member => member !== editedMember));
        setTeamMembers(prevArray => [...prevArray, newMember]);

        // Reset the edit states
        setEditFirstName('');
        setEditLastName('');
        setEditPhoneNumber('');
        setEditNotes('');
        closeEditMemberModal();
    }

    // Delete member
    function deleteMember() {
        setTeamMembers(teamMembers.filter(member => member !== editedMember));
        closeEditMemberModal();
    }

    // Create new team by adding team name with members to db
    const onClickCreateTeam = async () => {
        setIsLoading(true);
        // Create a new team document with an empty members subcollection
        const createTeam = async (teamName, userId) => {
            const newTeam = {
                name: teamName,
                userId: userId,
            };
            const newTeamDoc = await createNewDocInDb('Teams', newTeam);
            return newTeamDoc.id; // Return the ID of the new team document
        };

        // Add a new member to a team document's members subcollection
        const addMemberToTeam = async (teamId, memberData) => {
            await createNewSubcollectionDocInDb(
                'Teams',
                teamId,
                'Members',
                memberData
            );
        };

        const teamId = await createTeam(teamName, user.uid); // Create a new team document

        for (const member of teamMembers) {
            await addMemberToTeam(teamId, member); // Add a member to the team document's members subcollection
        };
        setIsLoading(false);
        navigate(`/teams`);
    };

    function onClickMissingRequirement() {
        if (teamName.length === 0 || memberRows.length === 0) {
            window.scrollTo({ top: 0, behavior: 'auto' });
            setErrMsg('Please enter a team name and at least one member.');
        }
    }

    // Displays correct member rows or message
    function displayMemberRows() {
        let displayedMemberRows;
        if (searchResults.length === 0 && memberRows.length === 0) {
            displayedMemberRows = 'No members added yet.';
        } else if (search.length === 0 && memberRows.length > 0) {
            displayedMemberRows = memberRows;
        } else if (searchResults.length === 0 && memberRows.length > 0) {
            displayedMemberRows = 'No members match search.';
        } else {
            displayedMemberRows = searchResults;
        }
        return displayedMemberRows;
    }

    const MemberRowContainer = styled('div')(() => ({
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1rem',
    }));

    const ErrMsgContainer = styled('span')(() => ({
        color: 'red',
        marginTop: '-12px',
        textAlign: 'center',
    }));

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                gap: '0.7rem',
            }}>

            {isLoading && <LoadingSpinner isLoading={isLoading} />}

            <h2>Create Team</h2>

            <div style={{ width: '20rem' }}>
                <TextInput
                    label={'Team Name'}
                    setText={setTeamName} />
            </div>

            {errMsg && <ErrMsgContainer>{errMsg}</ErrMsgContainer>}

            <ImportCSV setData={setCsvData} />

            <SearchBar
                placeholder={'Search Member'}
                search={search}
                setSearch={setSearch} />

            <MemberRowContainer>
                {displayMemberRows()}
            </MemberRowContainer>

            <Box
                sx={{
                    marginTop: '2%',
                    marginBottom: '2%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '15px',
                }} >
                <ButtonOutline
                    color={Colors.blue}
                    onClick={openAddMemberModal}>Add Member</ButtonOutline>

                <AddMemberModal
                    label={'Add Member'}
                    isVisible={addMemberModalVisible}
                    setIsVisible={setAddMemberModalVisible}
                    setFirstNameText={setFirstName}
                    setLastNameText={setLastName}
                    setPhoneNumText={setPhoneNumber}
                    setNotesText={setNotes}
                    text={notes}
                    onClickAddMember={addMember}
                    firstName={firstName}
                    lastName={lastName}
                    phoneNumber={phoneNumber} />

                <EditMemberModal
                    label={'Add Member'}
                    isVisible={editMemberModalVisible}
                    setIsVisible={setEditMemberModalVisible}
                    setFirstNameText={setEditFirstName}
                    setLastNameText={setEditLastName}
                    setPhoneNumText={setEditPhoneNumber}
                    setNotesText={setEditNotes}
                    firstNameValue={editFirstName}
                    lastNameValue={editLastName}
                    phoneNumberValue={editPhoneNumber}
                    notesValue={editNotes}
                    onClickSave={editMember}
                    onClickDelete={deleteMember}
                    firstName={editFirstName}
                    lastName={editLastName} />

                {teamName.length === 0 || memberRows.length === 0
                    ? <Button
                        color={Colors.grey2}
                        onClick={onClickMissingRequirement}>
                        Create Team
                    </Button>
                    : <Button
                        color={Colors.green}
                        onClick={onClickCreateTeam}>
                        Create Team
                    </Button>
                }
            </Box>
        </div>
    )
};

export default CreateTeam;